import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import calc from 'pared/Routes/renderer/utils/calc'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

type FwWingstopFlashSalesByOrderModeType =
  | 'avgOrderPrice'
  | 'orderPrice'
  | 'orderPercent'

interface IProps {
  modeType: FwWingstopFlashSalesByOrderModeType
}

interface INodeType {
  businessYear: number
  businessQuarter: number
  businessMonth: number
  businessWeek: number
  businessWeekOfMonth: number
  dateIndex: number
  metricData: Record<
    string,
    {
      name: string
      unit: string
      value: number
    }
  >
  metricSummaryData: Record<
    string,
    {
      name: string
      unit: string
      value: number
    }
  >
}

interface IGroupNode extends INodeType {
  locationGroupId: string
}

interface ILocationNode extends INodeType {
  locationId: string
}

type IDataType =
  | {
      trendLocationGroupMetricValuesByDateRangeNumber: {
        nodes: IGroupNode[]
      }
    }
  | {
      trendLocationMetricValuesByDateRangeNumber: {
        nodes: ILocationNode[]
      }
    }

const query = gql`
  query ListFwWingstopR365SalesByOrderMode(
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    trendLocationGroupMetricValuesByDateRangeNumber(
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
      iDateRangeNumber: 8
    ) @include(if: $hasGroupBy) {
      nodes {
        locationGroupId
        businessYear
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        dateIndex
        metricData
        metricSummaryData
      }
    }

    trendLocationMetricValuesByDateRangeNumber(
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
      iDateRangeNumber: 8
    ) @skip(if: $hasGroupBy) {
      nodes {
        locationId
        businessYear
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        dateIndex
        metricData
        metricSummaryData
      }
    }
  }
`

export const KPIS: {
  code: string
  name: string
  type: FwWingstopFlashSalesByOrderModeType
}[] = [
  // Average Weekly Sales by Order Mode
  {
    code: 'daily_avg_pnl_sales_dine_in_walk_in',
    name: 'Dine In - Walk In',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_take_out_walk_in',
    name: 'Take Out - Walk In',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_take_out_call_in_call_center',
    name: 'Take Out - Call in/Call Center',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_online_pick_up',
    name: 'Online (Pick Up)',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_online_dispatch',
    name: 'Online (Dispatch)',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_ubereats',
    name: 'UberEats',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_doordash',
    name: 'DoorDash',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_grubhub',
    name: 'Grubhub',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_other',
    name: 'Other',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_ubereats_markup',
    name: 'UberEats Markup',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_doordash_markup',
    name: 'DoorDash Markup',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_grubhub_markup',
    name: 'Grubhub Markup',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_other_markup',
    name: 'Other Markup',
    type: 'avgOrderPrice',
  },
  {
    code: 'daily_avg_pnl_sales_disounts_comps',
    name: 'Discounts / Comps',
    type: 'avgOrderPrice',
  },
  // Total Sales by Order Mode
  {
    code: 'pnl_sales_dine_in_walk_in',
    name: 'Dine In - Walk In',
    type: 'orderPrice',
  },
  {
    code: 'pnl_take_out_-_walk-in',
    name: 'Take Out - Walk In',
    type: 'orderPrice',
  },
  {
    code: 'pnl_sales_take_out_call_in_call_center',
    name: 'Take Out - Call in/Call Center',
    type: 'orderPrice',
  },
  {
    code: 'pnl_take_out_-_olo/mywingstop',
    name: 'Online (Pick Up)',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_olo/mywingstop',
    name: 'Online (Dispatch)',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_ubereats',
    name: 'UberEats',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_doordash',
    name: 'DoorDash',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_grubhub',
    name: 'Grubhub',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_other',
    name: 'Other',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_ubereats_markup',
    name: 'UberEats Markup',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_doordash_markup',
    name: 'DoorDash Markup',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_grubhub_markup',
    name: 'Grubhub Markup',
    type: 'orderPrice',
  },
  {
    code: 'pnl_delivery_-_other_markup',
    name: 'Other Markup',
    type: 'orderPrice',
  },
  {
    code: 'pnl_sales_disounts_comps',
    name: 'Discounts / Comps',
    type: 'orderPrice',
  },
  // Total Sales Percent by Order Mode
  {
    code: 'pnl_sales_dine_in_walk_in_percentage',
    name: 'Dine In - Walk In',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_take_out_walk_in_percentage',
    name: 'Take Out - Walk In',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_take_out_call_in_call_center_percentage',
    name: 'Take Out - Call in/Call Center',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_online_pick_up_percentage',
    name: 'Online (Pick Up)',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_online_dispatch_percentage',
    name: 'Online (Dispatch)',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_ubereats_percentage',
    name: 'UberEats',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_doordash_percentage',
    name: 'DoorDash',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_grubhub_percentage',
    name: 'Grubhub',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_other_percentage',
    name: 'Other',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_ubereats_markup_percentage',
    name: 'UberEats Markup',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_doordash_markup_percentage',
    name: 'DoorDash Markup',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_grubhub_markup_percentage',
    name: 'Grubhub Markup',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_other_markup_percentage',
    name: 'Other Markup',
    type: 'orderPercent',
  },
  {
    code: 'pnl_sales_disounts_comps_percentage',
    name: 'Discounts / Comps',
    type: 'orderPercent',
  },
]

export const fwWingstopFlashSalesByOrderModeConfigs = {
  // Average Weekly Sales by Order Mode
  avgOrderPriceName: 'string',
  avgOrderPriceWeek1InPast: 'price',
  avgOrderPriceWeek2InPast: 'price',
  avgOrderPriceWeek3InPast: 'price',
  avgOrderPriceWeek4InPast: 'price',
  avgOrderPriceWeek5InPast: 'price',
  avgOrderPriceWeek6InPast: 'price',
  avgOrderPriceWeek7InPast: 'price',
  avgOrderPriceWeek8InPast: 'price',

  // Total Sales by Delivery Mode
  orderPriceName: 'string',
  orderPriceWeek1InPast: 'price',
  orderPriceWeek2InPast: 'price',
  orderPriceWeek3InPast: 'price',
  orderPriceWeek4InPast: 'price',
  orderPriceWeek5InPast: 'price',
  orderPriceWeek6InPast: 'price',
  orderPriceWeek7InPast: 'price',
  orderPriceWeek8InPast: 'price',

  // Total Sales Percent by Order Mode
  orderPercentName: 'string',
  orderPercentWeek1InPast: 'percent',
  orderPercentWeek2InPast: 'percent',
  orderPercentWeek3InPast: 'percent',
  orderPercentWeek4InPast: 'percent',
  orderPercentWeek5InPast: 'percent',
  orderPercentWeek6InPast: 'percent',
  orderPercentWeek7InPast: 'percent',
  orderPercentWeek8InPast: 'percent',
} as const

const codeToType = (metricCode: string) =>
  KPIS.find((k) => k.code === metricCode)?.type || ''

const buildUseFwWingstopR365SalesByOrderMode = ({ modeType }: IProps) => {
  const useFwWingstopR365SalesByOrderMode = () => {
    const { endDate } = useDateFilter()
    const { groupFilter, hasGroupBy } = useGroupFilter()
    const { variables } = useVariables()

    const yesterday = moment().startOf('day').subtract(1, 'day')
    const adjustedEndDate =
      endDate && moment(endDate, 'YYYY-MM-DD').isAfter(yesterday)
        ? yesterday.format('YYYY-MM-DD')
        : endDate

    const dateType = variables.date?.value.type
    const dateRangeStartDate = variables.date?.getInfo(0)?.dateRange.startDate
    const iGroupBy = ((dateType?: string) => {
      switch (dateType) {
        case 'period':
          return 'last_x_periods'

        case 'quarter':
          return 'last_x_quarters'

        case 'year':
          return 'last_x_years'

        case 'week':
        case 'last_week':
        case 'this_week':
          return 'last_x_weeks'

        default:
          return 'last_x_days'
      }
    })(dateType)

    const { data, loading } = useQuery<IDataType>(query, {
      variables: {
        iEndDate: adjustedEndDate,
        iGroupBy,
        iFilter: {
          ...(hasGroupBy
            ? {
                location_group_ids: groupFilter?.ids,
              }
            : {
                location_ids: groupFilter?.ids,
              }),
          metrics: KPIS.map((k) => k.code),
        },
        hasGroupBy,
      },
      skip: !adjustedEndDate || !groupFilter,
    })

    return {
      data: useMemo((): IApiDataType => {
        if (!data) return null
        const customizedData =
          hasGroupBy &&
          'trendLocationGroupMetricValuesByDateRangeNumber' in data
            ? data?.trendLocationGroupMetricValuesByDateRangeNumber?.nodes
            : 'trendLocationMetricValuesByDateRangeNumber' in data &&
              data?.trendLocationMetricValuesByDateRangeNumber?.nodes

        if (!customizedData) return null

        const shiftedCustomizedData = dateRangeStartDate
          ?.clone()
          .subtract(1, 'days')
          .isSame(yesterday)
          ? customizedData.map((d) => ({
              ...d,
              dateIndex: d.dateIndex + 1,
            }))
          : customizedData

        let source: any[] = KPIS.filter((k) => k.type === modeType).map(
          (k) => ({
            id: k.code,
            parentId: 'root',
            metricCode: k.code,
            [`${k.type}Name`]: k.name,
            type: k.type,
          }),
        )
        const summary: { [key: string]: number | null } = {}

        shiftedCustomizedData.forEach((weeklyData) => {
          Object.keys(weeklyData.metricData).forEach((code) => {
            const currentSource = source.find((s) => s.metricCode === code)
            if (currentSource) {
              const newKey = `${codeToType(code)}Week${
                weeklyData.dateIndex
              }InPast`

              if (modeType === 'avgOrderPrice') {
                // It is daily average, need to times 7 to make it weekly
                switch (weeklyData.metricData[code].unit) {
                  case 'CENT':
                    currentSource[newKey] =
                      weeklyData.metricData[code].value * 7

                    if (!summary[newKey]) {
                      summary[newKey] =
                        0 + weeklyData.metricSummaryData[code].value * 7
                    } else {
                      summary[newKey] +=
                        weeklyData.metricSummaryData[code].value * 7
                    }

                    if (
                      moment(endDate).isAfter(yesterday) &&
                      weeklyData.dateIndex === 1
                    ) {
                      currentSource[newKey] = null
                      summary[newKey] = null
                    }
                    break

                  default:
                    currentSource[newKey] =
                      weeklyData.metricData[code].value * 100 * 7

                    if (!summary[newKey]) {
                      summary[newKey] =
                        0 + weeklyData.metricSummaryData[code].value * 100 * 7
                    } else {
                      summary[newKey] +=
                        weeklyData.metricSummaryData[code].value * 100 * 7
                    }

                    if (
                      moment(endDate).isAfter(yesterday) &&
                      weeklyData.dateIndex === 1
                    ) {
                      currentSource[newKey] = null
                      summary[newKey] = null
                    }
                    break
                }
              } else {
                switch (weeklyData.metricData[code].unit) {
                  case 'DOLLAR':
                  case 'PERCENTAGE':
                    currentSource[newKey] =
                      weeklyData.metricData[code].value * 100
                    if (!summary[newKey]) {
                      summary[newKey] =
                        0 + weeklyData.metricSummaryData[code].value * 100
                    } else {
                      summary[newKey] +=
                        weeklyData.metricSummaryData[code].value * 100
                    }
                    break

                  case 'COUNT':
                  case 'CENT':
                    currentSource[newKey] = weeklyData.metricData[code].value

                    if (!summary[newKey]) {
                      summary[newKey] =
                        0 + weeklyData.metricSummaryData[code].value
                    } else {
                      summary[newKey] +=
                        weeklyData.metricSummaryData[code].value
                    }
                    break
                }
              }
            }
          })
        })

        const discountsAndComps = source.find(
          (s) => s[`${modeType}Name`] === 'Discounts / Comps',
        )
        const discountsAndCompsAndMarkups = source
          .filter((s) => s[`${modeType}Name`].includes('Markup'))
          .reduce(
            (result, s) => ({
              ...result,
              ...Object.fromEntries(
                Object.entries(s).map(([k, v]) => [
                  k,
                  calc(result[k] || 0, '+', v || 0),
                ]),
              ),
            }),
            {},
          )

        return [
          ...source.filter((s) => s[`${modeType}Name`] !== 'Discounts / Comps'),
          {
            ...Object.fromEntries(
              Object.entries(summary).map(([k, v]) => [
                k,
                calc(v, '-', discountsAndComps[k]),
              ]),
            ),
            id: 'Total',
            parentId: 'root',
            [`${modeType}Name`]: 'Total',
          },
          discountsAndComps!,
          {
            ...summary,
            id: 'Net Total (excl. Comps)',
            parentId: 'root',
            [`${modeType}Name`]: 'Net Total (excl. Comps)',
          },
          {
            ...Object.fromEntries(
              Object.entries(summary).map(([k, v]) => [
                k,
                calc(v, '-', discountsAndCompsAndMarkups[k]),
              ]),
            ),
            id: 'Net Total (excl. Comps and Markups)',
            parentId: 'root',
            [`${modeType}Name`]: 'Net Total (excl. Comps and Markups)',
          },
        ]
      }, [data, variables]),
      loading,
    }
  }

  return useFwWingstopR365SalesByOrderMode
}

export const useFwWingstopR365AverageWeeklySalesbyOrderMode =
  buildUseFwWingstopR365SalesByOrderMode({
    modeType: 'avgOrderPrice',
  })

export const useFwWingstopR365TotalSalesbyOrderMode =
  buildUseFwWingstopR365SalesByOrderMode({
    modeType: 'orderPrice',
  })

export const useFwWingstopR365TotalSalesPercentbyOrderMode =
  buildUseFwWingstopR365SalesByOrderMode({
    modeType: 'orderPercent',
  })
